import { useEffect, useState, useCallback } from "react";
import { X, Mic, Volume2, RefreshCw, PlayCircle, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const LS_INPUT = "virtus_audio_input";
const LS_OUTPUT = "virtus_audio_output";

/**
 * Modal that lets the operator pick which mic + speaker the 60" TV station
 * routes voice through. Selections persist in localStorage and are consumed
 * by VoiceOrb (getUserMedia deviceId + HTMLAudioElement.setSinkId).
 */
export default function AudioSettingsModal({ open, onClose }) {
    const [inputs, setInputs] = useState([]);
    const [outputs, setOutputs] = useState([]);
    const [inputId, setInputId] = useState(localStorage.getItem(LS_INPUT) || "");
    const [outputId, setOutputId] = useState(localStorage.getItem(LS_OUTPUT) || "");
    const [permissionOK, setPermissionOK] = useState(false);
    const [supportsSinkId, setSupportsSinkId] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    const refresh = useCallback(async () => {
        setRefreshing(true);
        try {
            // Prompt for permission first so labels are populated.
            try {
                const s = await navigator.mediaDevices.getUserMedia({ audio: true });
                s.getTracks().forEach((t) => t.stop());
                setPermissionOK(true);
            } catch (e) {
                setPermissionOK(false);
            }
            const devices = await navigator.mediaDevices.enumerateDevices();
            setInputs(devices.filter((d) => d.kind === "audioinput"));
            setOutputs(devices.filter((d) => d.kind === "audiooutput"));
        } catch (e) {
            toast.error("Could not enumerate audio devices.");
        } finally {
            setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        if (!open) return;
        const audioEl = document.createElement("audio");
        setSupportsSinkId(typeof audioEl.setSinkId === "function");
        refresh();
    }, [open, refresh]);

    const save = () => {
        if (inputId) localStorage.setItem(LS_INPUT, inputId);
        else localStorage.removeItem(LS_INPUT);
        if (outputId) localStorage.setItem(LS_OUTPUT, outputId);
        else localStorage.removeItem(LS_OUTPUT);
        toast.success("Audio devices saved.");
        window.dispatchEvent(new CustomEvent("virtus:audio-devices-changed"));
        onClose();
    };

    const testSpeaker = async () => {
        try {
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            const dest = ctx.createMediaStreamDestination();
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = "sine";
            osc.frequency.value = 660;
            gain.gain.value = 0.15;
            osc.connect(gain).connect(dest);
            osc.start();

            const audio = new Audio();
            audio.srcObject = dest.stream;
            if (outputId && typeof audio.setSinkId === "function") {
                try {
                    await audio.setSinkId(outputId);
                } catch (e) {
                    toast.warning("Selected speaker rejected — falling back to default.");
                }
            }
            await audio.play();
            setTimeout(() => {
                osc.stop();
                audio.pause();
                ctx.close();
            }, 700);
        } catch (e) {
            toast.error("Speaker test failed.");
        }
    };

    const testMic = async () => {
        try {
            const constraints = {
                audio: inputId ? { deviceId: { exact: inputId } } : true,
            };
            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            const src = ctx.createMediaStreamSource(stream);
            const analyser = ctx.createAnalyser();
            analyser.fftSize = 256;
            src.connect(analyser);
            const buf = new Uint8Array(analyser.frequencyBinCount);

            let ticks = 0;
            let peak = 0;
            const t = setInterval(() => {
                analyser.getByteTimeDomainData(buf);
                const dev = Math.max(...buf.map((v) => Math.abs(v - 128)));
                peak = Math.max(peak, dev);
                setLevel(dev);
                ticks++;
                if (ticks > 40) {
                    clearInterval(t);
                    stream.getTracks().forEach((tr) => tr.stop());
                    ctx.close();
                    setLevel(0);
                    if (peak < 5) toast.warning("No audio detected. Check the selected mic.");
                    else toast.success(`Mic active. Peak: ${peak}`);
                }
            }, 50);
        } catch (e) {
            toast.error(
                e.name === "OverconstrainedError"
                    ? "Selected mic not available. Refresh devices."
                    : "Mic test failed. Check browser permissions."
            );
        }
    };

    const [level, setLevel] = useState(0);

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[85] flex items-center justify-center bg-black/85 backdrop-blur-md">
            <div className="relative w-[640px] max-w-[92vw] bg-[#0A0A0A] border-2 border-virtus-orange">
                <div className="absolute top-0 left-0 w-4 h-4 border-l-2 border-t-2 border-virtus-orange" />
                <div className="absolute top-0 right-0 w-4 h-4 border-r-2 border-t-2 border-virtus-orange" />
                <div className="absolute bottom-0 left-0 w-4 h-4 border-l-2 border-b-2 border-virtus-orange" />
                <div className="absolute bottom-0 right-0 w-4 h-4 border-r-2 border-b-2 border-virtus-orange" />

                <div className="flex items-center justify-between px-6 py-4 border-b border-virtus-line">
                    <div>
                        <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange">
                            AUDIO ROUTING // STATION HARDWARE
                        </div>
                        <div className="font-heading text-xl font-black uppercase tracking-tight mt-1">
                            Devices
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-virtus-dim hover:text-virtus-orange"
                        data-testid="audio-settings-close-btn"
                        aria-label="Close"
                    >
                        <X size={16} />
                    </button>
                </div>

                <div className="px-6 py-5 space-y-5">
                    {!permissionOK && (
                        <div className="flex items-start gap-3 border border-virtus-working px-3 py-2 bg-virtus-working/10">
                            <AlertTriangle size={14} className="text-virtus-working flex-shrink-0 mt-0.5" />
                            <div className="text-xs text-virtus-working">
                                Microphone permission not granted. Click Refresh and allow access
                                so device names appear.
                            </div>
                        </div>
                    )}

                    {/* Input */}
                    <div>
                        <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-dim mb-2">
                            <Mic size={12} className="text-virtus-orange" /> INPUT / MIC
                        </div>
                        <select
                            value={inputId}
                            onChange={(e) => setInputId(e.target.value)}
                            className="w-full bg-[#0A0A0A] border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white font-mono text-xs outline-none"
                            data-testid="audio-input-select"
                        >
                            <option value="">System default</option>
                            {inputs.map((d) => (
                                <option key={d.deviceId} value={d.deviceId}>
                                    {d.label || `Microphone (${d.deviceId.slice(0, 8)}…)`}
                                </option>
                            ))}
                        </select>
                        <div className="mt-2 flex items-center gap-3">
                            <button
                                onClick={testMic}
                                className="flex items-center gap-1 font-mono text-[9px] tracking-[0.22em] uppercase text-virtus-orange border border-virtus-orange px-2 py-1 hover:bg-virtus-orange hover:text-black"
                                data-testid="audio-test-mic-btn"
                            >
                                <PlayCircle size={11} /> TEST MIC
                            </button>
                            <div className="flex-1 h-2 bg-virtus-surface border border-virtus-line overflow-hidden">
                                <div
                                    className="h-full bg-virtus-orange transition-[width]"
                                    style={{ width: `${Math.min(100, (level / 60) * 100)}%` }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Output */}
                    <div>
                        <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-dim mb-2">
                            <Volume2 size={12} className="text-virtus-orange" /> OUTPUT / SPEAKER
                        </div>
                        <select
                            value={outputId}
                            onChange={(e) => setOutputId(e.target.value)}
                            className="w-full bg-[#0A0A0A] border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white font-mono text-xs outline-none disabled:opacity-60"
                            data-testid="audio-output-select"
                            disabled={!supportsSinkId}
                        >
                            <option value="">System default</option>
                            {outputs.map((d) => (
                                <option key={d.deviceId} value={d.deviceId}>
                                    {d.label || `Speaker (${d.deviceId.slice(0, 8)}…)`}
                                </option>
                            ))}
                        </select>
                        <div className="mt-2 flex items-center gap-3">
                            <button
                                onClick={testSpeaker}
                                className="flex items-center gap-1 font-mono text-[9px] tracking-[0.22em] uppercase text-virtus-orange border border-virtus-orange px-2 py-1 hover:bg-virtus-orange hover:text-black"
                                data-testid="audio-test-speaker-btn"
                            >
                                <PlayCircle size={11} /> TEST SPEAKER
                            </button>
                            {!supportsSinkId && (
                                <span className="font-mono text-[9px] tracking-[0.22em] uppercase text-virtus-working">
                                    Browser cannot route output — will use OS default.
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="border-t border-virtus-line pt-4 space-y-1">
                        <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase text-virtus-faint">
                            <CheckCircle2 size={11} className="text-virtus-done" />
                            Mic selection applies to voice recording and briefings.
                        </div>
                        <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase text-virtus-faint">
                            <CheckCircle2 size={11} className="text-virtus-done" />
                            Speaker selection routes all VIRTUS voice output.
                        </div>
                        <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase text-virtus-faint">
                            <AlertTriangle size={11} className="text-virtus-working" />
                            "Hey Virtus" wake word uses the OS default mic (browser limitation).
                        </div>
                    </div>
                </div>

                <div className="flex items-center justify-between px-6 py-3 border-t border-virtus-line">
                    <button
                        onClick={refresh}
                        disabled={refreshing}
                        className="flex items-center gap-2 font-mono text-[10px] tracking-[0.24em] uppercase text-virtus-dim hover:text-virtus-orange"
                    >
                        <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} /> REFRESH
                    </button>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={onClose}
                            className="font-mono text-[10px] tracking-[0.22em] uppercase text-virtus-dim hover:text-white px-3 py-2"
                        >
                            CANCEL
                        </button>
                        <button
                            onClick={save}
                            className="font-mono text-[10px] tracking-[0.22em] uppercase bg-virtus-orange text-black px-4 py-2 hover:bg-white glow-orange"
                            data-testid="audio-settings-save-btn"
                        >
                            SAVE
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export function getSelectedInputId() {
    return localStorage.getItem(LS_INPUT) || null;
}

export function getSelectedOutputId() {
    return localStorage.getItem(LS_OUTPUT) || null;
}
